import React, { Component, Fragment } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import Organization, { OrganizationObserver } from "data/organization/Organization";
import User from "data/organization/User";
import BackendFactory from "data/BackendFactory";
import PolicyGroup from "data/organization/PolicyGroup";
import { Maybe } from "types/aliases";
import NewUserForm from "./new-user-form";
import UserListItem from "./user-list-item";
import { UserOrganizationAction } from "./user-organization-action";
import Localization from "data/localization-sensoan/Localization";
import ConfirmationDialog from "components/ui/confirmation-dialog";
import ErrorDialog from "components/ui/error-dialog";
import Loader from "components/ui/loader";

// current user's permissions within the context of the current organization
interface UserPermissions {
  deleteUser: boolean;
  removeUser: boolean;
  adjustPolicies: boolean;
}

const NO_PERMISSIONS: UserPermissions = {
  removeUser: false,
  deleteUser: false,
  adjustPolicies: false,
};

interface Props {
  organization: Organization;
  currentUserId: string;
}

interface State {
  loading: boolean;
  users: User[];
  action?: {user: User; action: UserOrganizationAction};
  userPermissions?: UserPermissions;
  policyGroups?: PolicyGroup[];
  errorMsg?: string;
}

export default class UserList extends Component<Props, State> implements OrganizationObserver {
  private text = Localization.getInstance().getDisplayText;

  public constructor(props: Props) {
    super(props);
    this.state = {
      loading: false,
      users: [],
    };
  }

  public componentDidMount(): void {
    this.props.organization.addObserver(this);
    this.performUpdates();
  }

  public componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.organization.getId() !== this.props.organization.getId()) {
      prevProps.organization.removeObserver(this);
      this.performUpdates();
      this.props.organization.addObserver(this);
    }
  }

  public componentWillUnmount(): void {
    this.props.organization.removeObserver(this);
  }

  private performUpdates(): void {
    this.setState({
      loading: true,
      userPermissions: undefined,
      policyGroups: undefined,
      users: [],
    });
    Promise
      .allSettled([
        this.updateUserPermissions(),
        this.updateUsers(),
        this.updatePolicyGroups(),
      ])
      .finally(() => {
        this.setState({ loading: false });
      });
  }

  private async updateUserPermissions(): Promise<void> {
    try {
      const user = await BackendFactory.getOrganizationBackend().getCurrentUser();

      if (user) {
        const organizationId = this.props.organization.getId();
        const allowedActions = await Promise.all([
          user.hasGrants(organizationId, "usersDelete"),
          user.hasGrants(organizationId, "organizationsUsersRemove"),
          user.hasGrants(organizationId, "policyGroupsUsersAdd", "policyGroupsUsersRemove"),
        ]);

        this.setState({
          userPermissions: {
            deleteUser: allowedActions[0],
            removeUser: allowedActions[1],
            adjustPolicies: allowedActions[2],
          },
        });
      }
    } catch (err) {
      console.error("updateUserPermissions", err);
      this.setState({ userPermissions: NO_PERMISSIONS });
    }
  }

  private async updateUsers(): Promise<void> {
    try {
      const users = await this.props.organization.getUsers();
      this.setState({
        users,
      });
    } catch (err) {
      console.error("updateUsers", err);
      this.setState({
        users: [],
      });
    }
  }

  private async updatePolicyGroups(): Promise<void> {
    try {
      const policyGroups = await this.props.organization.getPolicyGroups();
      policyGroups.sort(PolicyGroup.compareByPolicies);
      this.setState({ policyGroups });
    } catch (err) {
      console.error("updatePolicyGroups", err);
      this.setState({ policyGroups: [] });
    }
  }

  public onUsersChange(usersList: User[]): void {
    this.setState({
      users: usersList,
      loading: false,
    });
  }

  private handleUserAction = (action: UserOrganizationAction, user: User): void => {
    this.setState({
      action: {
        user,
        action,
      },
    });
  };

  private handleSubmitAction = async (): Promise<void> => {
    const action = this.state.action;

    if (!action) {
      return;
    }

    this.setState({ loading: true, action: undefined });

    try {
      if (action.action === UserOrganizationAction.RemoveFromOrganization) {
        await this.props.organization.removeUser(action.user);
      } else if (action.action === UserOrganizationAction.DeleteUser) {
        await action.user.delete();
        this.setState({ loading: false });
      }
    } catch (error) {
      this.setState({
        loading: false,
        errorMsg: this.text("AdminView", "failedToDeleteUser"),
      });
    }
  };

  private handleCancelAction = (): void => {
    this.setState({ action: undefined });
  };

  private handleCloseErrorNote = (): void => {
    this.setState({ errorMsg: undefined });
  };

  private getUsersTableItems(): JSX.Element | JSX.Element[] {
    const users = this.state.users;
    const permissions = this.state.userPermissions;
    const organizationPolicies = this.state.policyGroups;

    const getUserAction = (user: User): UserOrganizationAction => {
      if (user.getId() === this.props.currentUserId) {
        return UserOrganizationAction.Nothing;
      } else if (user.getHomeOrganizationId() === this.props.organization.getId()) {
        return permissions?.deleteUser ? UserOrganizationAction.DeleteUser : UserOrganizationAction.Nothing;
      } else {
        return permissions?.removeUser ? UserOrganizationAction.RemoveFromOrganization : UserOrganizationAction.Nothing;
      }
    };

    if (organizationPolicies && permissions && users) {
      return users.map(user => {
        return (
          <UserListItem
            key={user.getId()}
            user={user}
            userAction={getUserAction(user)}
            organizationId={this.props.organization.getId()}
            availablePolicyGroups={organizationPolicies}
            actionCallback={this.handleUserAction}
            enablePolicySelection={user.getId() !== this.props.currentUserId && permissions.adjustPolicies}
          />
        );
      });
    } else {
      return (
        <tr>
          <td className="users-list-empty-text">{this.text("AdminView", "noUsers")}</td>
        </tr>
      );
    }
  }

  private renderUsersList = (): JSX.Element | JSX.Element[] => {
    if (this.state.loading) {
      return <Loader/>;
    } else if (this.state.errorMsg) {
      return (
        <ErrorDialog
          errorMsg={this.state.errorMsg}
          onClose={this.handleCloseErrorNote}
        />
      );
    } else {
      return (
        <Table className="org-users-table" data-testid="users-table">
          <TableHead>
            <TableRow>
              <TableCell>{this.text("Common", "username")}</TableCell>
              <TableCell align="center">{this.text("AdminView", "policyGroup")}</TableCell>
              <TableCell/>
            </TableRow>
          </TableHead>
          <TableBody>
            {this.getUsersTableItems()}
          </TableBody>
        </Table>
      );
    }
  };

  private renderActionPopup(): Maybe<JSX.Element> {
    if (!this.state.action) {
      return;
    }
    const title = this.state.action.action === UserOrganizationAction.DeleteUser
      ? this.text("AdminView", "deleteUser")
      : this.state.action.action === UserOrganizationAction.RemoveFromOrganization
        ? this.text("AdminView", "removeUserFromOrganization")
        : undefined;

    if (!title) {
      throw new Error("Unknown action selected!");
    }

    if (this.state.action) {
      return (
        <ConfirmationDialog
          title={title}
          message={this.state.action.user.getUsername()}
          onConfirm={this.handleSubmitAction}
          onCancel={this.handleCancelAction}
        />
      );
    }
  }

  public render(): JSX.Element {
    return (
      <Fragment>
        {
          this.state.policyGroups &&
          <NewUserForm
            showAccessError={false}
            showLoader={false}
            contextOrganization={this.props.organization}
            organization={this.props.organization}
            availablePolicyGroups={this.state.policyGroups}
          />
        }
        {this.renderUsersList()}
        {this.renderActionPopup()}
      </Fragment>
    );
  }
}
